import { useState } from 'react';
import { aboutInfo, categories } from '@/mocks/data';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于奶妈动漫',
    description: aboutInfo.brand.intro,
    url: 'https://naimadongman.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brand.name,
    description: aboutInfo.brand.intro,
    url: 'https://naimadongman.com',
    email: aboutInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
    },
    foundingDate: '2024',
    member: aboutInfo.team.map((m) => ({
      '@type': 'Person',
      name: m.name,
      jobTitle: m.role,
    })),
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9sn37d8o1vn02ekui8g', {
        method: 'POST',
        body: formData,
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setErrorMsg(msg);
        setFormStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '关于我们' },
              ]}
            />

            {/* Brand Intro */}
            <section className="mt-8 mb-16">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6">关于奶妈动漫</h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-foreground-700 leading-relaxed mb-4">{aboutInfo.brand.intro}</p>
                  <p className="text-foreground-700 leading-relaxed">{aboutInfo.brand.story}</p>
                </div>
                <div className="relative w-full h-64 lg:h-full min-h-[280px] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20creative%20studio%20workspace%20with%20anime%20style%20illustrations%20on%20walls%2C%20drawing%20tablets%2C%20soft%20natural%20light%2C%20potted%20plants%2C%20pastel%20warm%20tones%2C%20cozy%20atmosphere%2C%20clean%20cream%20background&width=800&height=600&seq=about-brand-image&orientation=landscape"
                    alt="奶妈动漫工作室"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">我们的理念</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.brand.values.map((v, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className={`${['ri-heart-line', 'ri-shield-check-line', 'ri-lightbulb-flash-line', 'ri-global-line'][i]} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-2">{v.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16 bg-accent-100/60 rounded-lg py-10 px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {aboutInfo.stats.map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl md:text-3xl font-heading text-accent-600 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Team */}
            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">核心团队</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.team.map((member, i) => {
                  const avatars = [
                    'https://readdy.ai/api/search-image?query=A%20cute%20warm%20anime%20style%20portrait%20avatar%20of%20a%20creative%20female%20professional%20with%20gentle%20smile%2C%20soft%20pastel%20colors%2C%20clean%20flat%20design%20on%20cream%20background&width=120&height=120&seq=team-0&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=A%20cute%20warm%20anime%20style%20portrait%20avatar%20of%20an%20artist%20with%20a%20paintbrush%2C%20soft%20pastel%20colors%2C%20clean%20flat%20design%20on%20cream%20background&width=120&height=120&seq=team-1&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=A%20cute%20warm%20anime%20style%20portrait%20avatar%20of%20a%20doctor%20with%20stethoscope%2C%20soft%20pastel%20colors%2C%20clean%20flat%20design%20on%20cream%20background&width=120&height=120&seq=team-2&orientation=squarish',
                    'https://readdy.ai/api/search-image?query=A%20cute%20warm%20anime%20style%20portrait%20avatar%20of%20a%20content%20creator%20with%20headphones%2C%20soft%20pastel%20colors%2C%20clean%20flat%20design%20on%20cream%20background&width=120&height=120&seq=team-3&orientation=squarish',
                  ];
                  return (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                    <img
                      src={avatars[i]}
                      alt={member.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                    />
                    <h3 className="font-heading text-base text-foreground-900 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-500 font-medium mb-2">{member.role}</p>
                    <p className="text-sm text-foreground-600">{member.desc}</p>
                  </div>
                  );
                })}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">微博</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">微信</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-1">地址</p>
                      <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <h3 className="font-heading text-lg text-foreground-900 mb-5">发送消息</h3>
                  {formStatus === 'success' ? (
                    <div className="py-12 text-center">
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-foreground-800 font-medium">消息已发送！</p>
                      <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} data-readdy-form="">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-primary-400 transition-colors resize-none"
                            placeholder="请输入您的消息（500字以内）"
                          ></textarea>
                        </div>
                        <button
                          type="submit"
                          className="px-6 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                        >
                          发送消息
                        </button>
                      </div>
                      {formStatus === 'error' && errorMsg && (
                        <p className="text-sm text-red-500 mt-3">{errorMsg}</p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </section>

            {/* Explore Links */}
            <section className="text-center py-8 border-t border-background-200/70">
              <h3 className="font-heading text-lg text-foreground-900 mb-4">探索更多内容</h3>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}